import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Heading, Modal, ModalOverlay } from 'react-aria-components'
import {
  ArrowNarrowLeftIcon,
  Expand02Icon,
  LayoutRightIcon,
  Stars02Icon,
  Tool02Icon,
  XCloseIcon,
} from '@untitledui-icons/react/line'
import { LoadingSpinner } from '@/components/common'
import { usePermissions } from '@/hooks/api'
import { useAssistant } from '@/hooks/api/useAssistants'
import {
  usePartnerReview,
  useSubmitPartnerReview,
  type SubmitPartnerReviewInput,
} from '@/hooks/api/usePartnerReview'
import { getErrorMessage } from '@/lib/api'
import { isWhatsAppOrigin } from '@/lib/conversationChannel'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { ToolCallDisplay } from '../components/ToolCallDisplay'
import {
  ReviewBanner,
  ReviewFlagButton,
  ReviewForm,
  type TranscriptReviewProps,
} from '../components/TranscriptReview'
import { InboxDetails, type InboxDetailsTab } from './InboxDetails'
import { getInboxTitle } from './inboxTitle'
import { splitTranscript, type TranscriptLine } from './splitTranscript'
import type { Assistant } from '@/types/assistant'
import type { Conversation } from '@/types/conversation'

type CardKind = 'phone' | 'whatsapp'

interface InboxThreadProps {
  conversation?: Conversation
  isLoading: boolean
  isDetailsOpen: boolean
  /** Details panel covers the thread, so header actions need room beside it */
  detailsOverlay?: boolean
  onToggleDetails: () => void
  onBack?: () => void
  /** Shows transcripts and details as tabs in one column instead of thread cards */
  tabs?: {
    assistant?: Assistant
    tab: InboxDetailsTab
    onTabChange: (tab: InboxDetailsTab) => void
  }
  className?: string
}

function SummaryBlock({ summary }: { summary?: string }) {
  const { t } = useTranslation()
  const textRef = useRef<HTMLParagraphElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isClamped, setIsClamped] = useState(false)

  useLayoutEffect(() => {
    const element = textRef.current
    if (!element || isOpen) return
    const measure = () => setIsClamped(element.scrollHeight > element.clientHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [summary, isOpen])

  return (
    <div className="flex w-full flex-col gap-2 rounded-[16px] border border-[#E7E5E4] bg-[#F2EBE7] p-4">
      <div className="flex items-center gap-2">
        <Stars02Icon
          className="size-[15px] shrink-0 text-[#44403C]"
          strokeWidth={2.5}
          aria-hidden="true"
        />
        <span className="text-sm font-medium text-[#44403C]">
          {t('resources.conversations.inbox.summary')}
        </span>
      </div>
      <p
        ref={textRef}
        className={cn('text-sm leading-5 text-[#44403C]', !isOpen && 'line-clamp-3')}
      >
        {summary || t('resources.conversations.inbox.no_summary')}
      </p>
      {(isClamped || isOpen) && (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          className="cursor-pointer self-start text-xs font-medium text-[#44403C] underline-offset-2 hover:underline"
        >
          {isOpen
            ? t('resources.conversations.inbox.notes.show_less')
            : t('resources.conversations.inbox.notes.show_more')}
        </button>
      )}
    </div>
  )
}

interface TranscriptProps {
  lines: TranscriptLine[]
  conversationDate?: string
  assistantConfig?: unknown
  review?: TranscriptReviewProps
}

function lineKey(line: TranscriptLine): string {
  if (line.role === 'tool_call') return line.toolCall.id
  if (line.role === 'whatsapp_sent') return `sent-${line.toolCallId}`
  return String(line.index)
}

/** Review controls go on the first visible line of each assistant message */
function lineReview(
  lines: TranscriptLine[],
  position: number,
  review?: TranscriptReviewProps
): TranscriptReviewProps | undefined {
  const line = lines[position]
  // A message sent during the call is reviewed on its tool call in the phone transcript
  if (!review || line.role === 'user' || line.role === 'whatsapp_sent') return undefined
  if (position > 0 && lines[position - 1].index === line.index) return undefined
  return review
}

function ReviewableTurn({
  index,
  review,
  footerClassName,
  children,
}: {
  index: number
  review?: TranscriptReviewProps
  footerClassName?: string
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(false)
  const existingReview = review?.reviews.find((entry) => entry.message_index === index)
  if (!review || (!review.reviewableIndices.includes(index) && !existingReview)) {
    return <>{children}</>
  }
  const isFlagged = existingReview?.flagged ?? false

  return (
    <div>
      <div className="flex items-start gap-1">
        <div className="min-w-0 flex-1">{children}</div>
        <ReviewFlagButton
          isFlagged={isFlagged}
          onPress={() => setIsOpen(!isOpen)}
          className="mt-1.5 shrink-0"
        />
      </div>
      {isFlagged && !isOpen && existingReview?.comment && (
        <p className={cn('text-xs text-red-600 dark:text-red-400', footerClassName)}>
          {existingReview.comment}
        </p>
      )}
      {isOpen && (
        <div className={cn('mt-1', footerClassName)}>
          <ReviewForm
            existingReview={existingReview}
            onCancel={() => setIsOpen(false)}
            onSubmit={async (flagged, comment) => {
              await review.onSubmit({ messageIndex: index, flagged, comment })
              setIsOpen(false)
            }}
          />
        </div>
      )}
    </div>
  )
}

function PhoneTranscript({ lines, conversationDate, assistantConfig, review }: TranscriptProps) {
  const { t } = useTranslation()
  return (
    <div className="mt-4 w-full">
      {lines.map((line, position) => (
        <ReviewableTurn
          key={lineKey(line)}
          index={line.index}
          review={lineReview(lines, position, review)}
          footerClassName="pb-2 pl-25"
        >
          {line.role === 'tool_call' ? (
            <div className="py-1 pl-25">
              <ToolCallDisplay
                toolCall={line.toolCall}
                response={line.response}
                conversationDate={conversationDate}
                assistantConfig={assistantConfig}
                className="mt-0"
              />
            </div>
          ) : (
            <div className="flex items-start py-2 text-sm leading-5">
              <span className="w-21 shrink-0 text-gray-400 dark:text-gray-500">
                {line.role === 'user'
                  ? t('resources.conversations.inbox.caller')
                  : t('resources.conversations.inbox.assistant')}
              </span>
              <p
                className={cn(
                  'min-w-0 flex-1 pl-4 wrap-anywhere whitespace-pre-line',
                  line.role === 'user'
                    ? 'font-semibold text-gray-900 dark:text-gray-100'
                    : 'text-gray-500 dark:text-gray-400'
                )}
              >
                {line.content}
              </p>
            </div>
          )}
        </ReviewableTurn>
      ))}
    </div>
  )
}

function WhatsAppBubbles({ lines, conversationDate, assistantConfig, review }: TranscriptProps) {
  const { t } = useTranslation()
  return (
    <div className="flex w-full flex-col gap-6">
      {lines.map((line, position) => (
        <ReviewableTurn
          key={lineKey(line)}
          index={line.index}
          review={lineReview(lines, position, review)}
          footerClassName="ml-auto w-[min(411px,86%)]"
        >
          {line.role === 'tool_call' ? (
            <div className="flex justify-end">
              <ToolCallDisplay
                toolCall={line.toolCall}
                response={line.response}
                conversationDate={conversationDate}
                assistantConfig={assistantConfig}
                className="mt-0 w-[min(411px,86%)]"
              />
            </div>
          ) : line.role === 'whatsapp_sent' ? (
            <div className="flex flex-col items-end gap-1">
              <p className="max-w-[min(411px,86%)] rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm leading-5 wrap-anywhere whitespace-pre-line text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100">
                {line.content}
              </p>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {t('resources.conversations.inbox.sent_during_call')}
              </span>
            </div>
          ) : (
            <div className={cn('flex', line.role === 'user' ? 'justify-start' : 'justify-end')}>
              <p
                className={cn(
                  'max-w-[min(411px,86%)] rounded-xl border p-3 text-sm leading-5 wrap-anywhere whitespace-pre-line text-gray-900 dark:text-gray-100',
                  line.role === 'user'
                    ? 'border-gray-100 bg-blue-50 dark:border-gray-800 dark:bg-blue-950/40'
                    : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
                )}
              >
                {line.content}
              </p>
            </div>
          )}
        </ReviewableTurn>
      ))}
    </div>
  )
}

function ThreadCard({
  title,
  onExpand,
  children,
}: {
  title: string
  onExpand: () => void
  children: React.ReactNode
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1 text-[13px] leading-[18px] font-medium text-gray-600 dark:text-gray-400">
          {title}
        </span>
        <button
          type="button"
          onClick={onExpand}
          aria-label={t('resources.conversations.inbox.expand', { title })}
          title={t('resources.conversations.inbox.expand', { title })}
          className="mt-0.5 cursor-pointer text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <Expand02Icon className="size-3.5" strokeWidth={2.5} aria-hidden="true" />
        </button>
      </div>
      {children}
    </div>
  )
}

const headerIconButtonClass =
  'flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full p-1.5 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800'

export function InboxThread({
  conversation,
  isLoading,
  isDetailsOpen,
  detailsOverlay = false,
  onToggleDetails,
  onBack,
  tabs,
  className,
}: InboxThreadProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<CardKind | null>(null)
  const storedShowToolCalls = useUIStore((state) => state.showToolCalls)
  const setShowToolCalls = useUIStore((state) => state.setShowToolCalls)
  // The mobile layout has no toggle, so tool calls stay visible there without
  // changing the preference the desktop button reads.
  const showToolCalls = Boolean(tabs) || storedShowToolCalls

  // The full assistant (with config) is only needed for tool call screenshot links
  const { data: assistant } = useAssistant(conversation?.assistant_id, {
    enabled: showToolCalls && Boolean(conversation?.assistant_id),
  })

  // Per-turn partner review is admin-only; the backend decides whether the conversation qualifies
  const { can } = usePermissions()
  const canReview = can('access:admin-panel')
  const { data: partnerReview } = usePartnerReview(conversation?.id, { enabled: canReview })
  const submitPartnerReview = useSubmitPartnerReview(conversation?.id)

  const review: TranscriptReviewProps | undefined =
    canReview && partnerReview?.reviewable
      ? {
          reviewableIndices: partnerReview.reviewable_indices,
          reviews: partnerReview.reviews,
          onSubmit: async (input: SubmitPartnerReviewInput) => {
            try {
              await submitPartnerReview.mutateAsync(input)
              toast.success(t('resources.conversations.transcript.review.saved'))
            } catch (error) {
              toast.error(getErrorMessage(error))
              throw error
            }
          },
        }
      : undefined

  const transcript = useMemo(() => {
    if (!conversation) return { phone: [], whatsapp: [] }
    const split = splitTranscript(conversation.messages, isWhatsAppOrigin(conversation))
    if (showToolCalls) return split
    const isSpoken = (line: TranscriptLine) => line.role !== 'tool_call'
    return { phone: split.phone.filter(isSpoken), whatsapp: split.whatsapp.filter(isSpoken) }
  }, [conversation, showToolCalls])

  const titles: Record<CardKind, string> = {
    phone: t('resources.conversations.inbox.phone_call'),
    whatsapp: t('resources.conversations.inbox.whatsapp_messages'),
  }

  // The summary sits in the first card; a WhatsApp-only conversation has no phone card
  const summaryCard: CardKind =
    transcript.phone.length || !transcript.whatsapp.length ? 'phone' : 'whatsapp'

  const renderBody = (kind: CardKind, includeSummary = true) => {
    const lines = kind === 'phone' ? transcript.phone : transcript.whatsapp
    return (
      <div className="w-full">
        {includeSummary && kind === summaryCard && (
          <div className={kind === 'whatsapp' ? 'mb-4' : undefined}>
            <SummaryBlock key={conversation?.id} summary={conversation?.summary} />
          </div>
        )}
        {kind === 'phone' ? (
          lines.length ? (
            <PhoneTranscript
              lines={lines}
              conversationDate={conversation?.created_at}
              assistantConfig={assistant?.config}
              review={review}
            />
          ) : (
            <p className="mt-4 text-center text-sm text-gray-500">
              {t('resources.conversations.no_transcript_available')}
            </p>
          )
        ) : (
          <WhatsAppBubbles
            lines={lines}
            conversationDate={conversation?.created_at}
            assistantConfig={assistant?.config}
            review={review}
          />
        )}
      </div>
    )
  }

  const cards: CardKind[] = []
  if (summaryCard === 'phone') cards.push('phone')
  if (transcript.whatsapp.length) cards.push('whatsapp')

  return (
    <section className={cn('flex min-h-0 min-w-0 flex-col', className)}>
      <div
        className={cn(
          'flex h-15 shrink-0 items-center gap-2 border-b border-gray-200 bg-white pl-4 dark:border-gray-800 dark:bg-gray-900',
          // w-86 overlay, plus the header's usual 1rem inset so the buttons clear the panel
          detailsOverlay ? 'pr-[calc(21.5rem+1rem)]' : 'pr-4'
        )}
      >
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label={t('resources.conversations.inbox.back')}
            className="flex size-7 cursor-pointer items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <ArrowNarrowLeftIcon className="size-4.5" aria-hidden="true" />
          </button>
        )}
        <h2 className="min-w-0 flex-1 truncate text-lg font-semibold text-gray-900 first-letter:uppercase dark:text-gray-100">
          {conversation ? getInboxTitle(conversation, t) : ''}
        </h2>
        {conversation && !tabs && (
          <button
            type="button"
            onClick={() => setShowToolCalls(!storedShowToolCalls)}
            aria-label={t('resources.conversations.inbox.show_tool_calls')}
            aria-pressed={storedShowToolCalls}
            title={t('resources.conversations.inbox.show_tool_calls')}
            className={cn(
              headerIconButtonClass,
              storedShowToolCalls && 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
            )}
          >
            <Tool02Icon className="size-5" aria-hidden="true" />
          </button>
        )}
        {conversation && !tabs && (
          <button
            type="button"
            onClick={onToggleDetails}
            aria-label={
              isDetailsOpen
                ? t('resources.conversations.inbox.collapse_details')
                : t('resources.conversations.inbox.show_details')
            }
            aria-pressed={isDetailsOpen}
            title={
              isDetailsOpen
                ? t('resources.conversations.inbox.collapse_details')
                : t('resources.conversations.inbox.show_details')
            }
            className={cn(
              headerIconButtonClass,
              isDetailsOpen && 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-100'
            )}
          >
            <LayoutRightIcon className="size-5" aria-hidden="true" />
          </button>
        )}
      </div>

      {tabs && conversation && !isLoading ? (
        <InboxDetails
          key={conversation.id}
          conversation={conversation}
          assistant={tabs.assistant}
          tab={tabs.tab}
          onTabChange={tabs.onTabChange}
          transcriptTabs={{
            ...(cards.includes('phone') && { phone: renderBody('phone') }),
            ...(cards.includes('whatsapp') && { whatsapp: renderBody('whatsapp') }),
          }}
          embedded
          className="flex-1"
        />
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2 pb-4">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : !conversation ? (
            <p className="py-8 text-center text-sm text-gray-500">
              {t('resources.conversations.inbox.select_conversation')}
            </p>
          ) : (
            <>
              {review && <ReviewBanner reviews={review.reviews} />}
              {cards.map((kind) => (
                <div key={kind} className="flex flex-col gap-2">
                  {kind === summaryCard && (
                    <SummaryBlock key={conversation.id} summary={conversation.summary} />
                  )}
                  <ThreadCard title={titles[kind]} onExpand={() => setExpanded(kind)}>
                    {renderBody(kind, false)}
                  </ThreadCard>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      <ModalOverlay
        isOpen={expanded !== null}
        onOpenChange={(isOpen) => !isOpen && setExpanded(null)}
        isDismissable
        className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/40 p-4"
      >
        <Modal className="flex max-h-[calc(100vh-5rem)] w-200 max-w-full flex-col rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 outline-hidden dark:bg-gray-900 dark:ring-gray-800">
          <Dialog className="flex min-h-0 flex-col gap-2 px-4 py-3 outline-hidden">
            {({ close }) => (
              <>
                <div className="flex items-start gap-2">
                  <Heading
                    slot="title"
                    className="min-w-0 flex-1 text-[13px] leading-[18px] font-medium text-gray-600 dark:text-gray-400"
                  >
                    {expanded && titles[expanded]}
                  </Heading>
                  <button
                    type="button"
                    onClick={close}
                    aria-label={t('resources.generic.close')}
                    className="mt-0.5 cursor-pointer text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    <XCloseIcon className="size-3.5" aria-hidden="true" />
                  </button>
                </div>
                <div className="min-h-0 overflow-y-auto">{expanded && renderBody(expanded)}</div>
              </>
            )}
          </Dialog>
        </Modal>
      </ModalOverlay>
    </section>
  )
}
